import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import logoUrl from "@agile/brand/assets/logos/agile-mark.svg";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useDocumentTitle } from "../lib/useDocumentTitle";

type Mode = "password" | "magic" | "forgot";

export default function Login() {
  useDocumentTitle("Sign in");
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      await refresh(); // sets the user → App swaps to the authed shell
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitMagic(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request", { email });
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <img className="login__logo" src={logoUrl} alt="Agile Software" />
        <h1 style={{ fontSize: 24 }}>Sign in</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Agile Software project portal
        </p>

        {params.get("error") && !sent && (
          <p className="notice notice--error" style={{ marginTop: 16 }}>
            That sign-in link was invalid or expired. Sign in below.
          </p>
        )}

        {sent ? (
          <p className="notice notice--ok" style={{ marginTop: 16 }}>
            {mode === "forgot" ? (
              <>
                If <strong>{email}</strong> has an account, a password-reset
                link is on its way. Check your inbox — the link expires in 60
                minutes.
              </>
            ) : (
              <>
                If <strong>{email}</strong> has access, a sign-in link is on its
                way. Check your inbox — the link expires in 15 minutes.
              </>
            )}
          </p>
        ) : mode === "forgot" ? (
          <form
            onSubmit={submitForgot}
            className="field"
            style={{ marginTop: 20, textAlign: "left" }}
          >
            <label className="label" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
            />
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={busy}
              style={{ marginTop: 8 }}
            >
              {busy ? "Sending…" : "Email me a reset link"}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--block"
              style={{ marginTop: 8 }}
              onClick={() => setMode("password")}
            >
              Back to sign in
            </button>
          </form>
        ) : mode === "password" ? (
          <form
            onSubmit={submitPassword}
            className="field"
            style={{ marginTop: 20, textAlign: "left" }}
          >
            {error && (
              <p className="notice notice--error" style={{ marginBottom: 8 }}>
                {error}
              </p>
            )}
            <label className="label" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
            />
            <label className="label" htmlFor="password" style={{ marginTop: 12 }}>
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              autoComplete="current-password"
            />
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={busy}
              style={{ marginTop: 12 }}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--block"
              style={{ marginTop: 8 }}
              onClick={() => {
                setMode("magic");
                setError("");
              }}
            >
              Email me a sign-in link instead
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--block"
              style={{ marginTop: 8 }}
              onClick={() => {
                setMode("forgot");
                setError("");
              }}
            >
              Forgot password?
            </button>
          </form>
        ) : (
          <form
            onSubmit={submitMagic}
            className="field"
            style={{ marginTop: 20, textAlign: "left" }}
          >
            <label className="label" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
            />
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={busy}
              style={{ marginTop: 8 }}
            >
              {busy ? "Sending…" : "Email me a sign-in link"}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--block"
              style={{ marginTop: 8 }}
              onClick={() => setMode("password")}
            >
              Sign in with a password instead
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
